import { getIntl, history } from "@umijs/max";
import { AppstoreOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { AdminShellActions, buildAdminAvatarProps, registerChunkLoadErrorReloadOnce } from "@airange/ui";
import { resolveUmiSsoGuardRedirect } from "@airange/ui/umi-sso-guard";
import { App as AntdApp, ConfigProvider } from "antd";
import type { ReactNode } from "react";
import "antd/dist/reset.css";
import { ApiError } from "@airange/shared/api";
import { buildDocumentTitle } from "@airange/shared/page-titles";
import { logAirangeBuildInfo } from "@airange/shared/browser-build-info";
import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import defaultSettings from "@/app/default-settings";
import { LocaleSwitch } from "./components/LocaleSwitch";
import { canAccessRedblueAdmin } from "./access";
import { PORTAL_BASE_URL } from "./lib/env";
import { initializeRedblueAdminLocale } from "./lib/locale";
import { toRedblueDisplayUser } from "./lib/redblue-api-contract";
import { bootstrapSession, logout } from "./lib/sso";
import { toAppLocalPathname, toBrowserPath } from "./lib/public-path";
import "./styles.css";

logAirangeBuildInfo(__AIRANGE_BUILD_INFO__);

const loginPath = LOGIN_PATH;

type LayoutMenuItem = {
  children?: LayoutMenuItem[];
  disabledTooltip?: boolean;
  [key: string]: unknown;
};

type PageTitleRenderProps = {
  location?: {
    pathname?: string;
  };
  title?: string | false;
  pageName?: string;
};

function disableMenuItemTooltips(items: LayoutMenuItem[] | undefined): LayoutMenuItem[] | undefined {
  if (!items) return items;
  return items.map((item) => ({
    ...item,
    disabledTooltip: true,
    children: disableMenuItemTooltips(item.children),
  }));
}

export async function getInitialState() {
  initializeRedblueAdminLocale();
  const settings = defaultSettings as Partial<LayoutSettings>;
  const pathname = readCurrentPathname();
  if (pathname === OIDC_CALLBACK_PATH) {
    return { settings, authStatus: "no_session" as const, currentUser: null };
  }
  try {
    const currentUser = await bootstrapSession();
    if (!currentUser) {
      return { settings, authStatus: "no_session" as const, currentUser: null };
    }
    if (!canAccessRedblueAdmin(currentUser)) {
      return { settings, authStatus: "no_access" as const, currentUser };
    }
    return { settings, authStatus: "authenticated" as const, currentUser };
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      return { settings, authStatus: "no_access" as const, currentUser: null };
    }
    throw error;
  }
}

function readCurrentPathname(): string {
  if (typeof window !== "undefined") {
    return toAppLocalPathname(window.location.pathname || "/");
  }
  return "/";
}

function readCurrentSearch(): string {
  if (typeof window !== "undefined") {
    return window.location.search || "";
  }
  return "";
}

function readCurrentHash(): string {
  if (typeof window !== "undefined") {
    return window.location.hash || "";
  }
  return "";
}

export const layout = ({ initialState }: { initialState?: Awaited<ReturnType<typeof getInitialState>> }) => {
  const currentUser = initialState?.currentUser ?? null;
  const displayUser = currentUser ? toRedblueDisplayUser(currentUser) : null;
  const hasAccess = initialState?.authStatus === "authenticated" && canAccessRedblueAdmin(currentUser);
  const displayName = displayUser?.full_name || displayUser?.username || "RedBlue";
  const intl = getIntl();
  const centerTitle = intl.formatMessage({ id: "redblue.shell.title" });
  const siteTitle = intl.formatMessage({ id: "platform.admin.title" });
  const loginPageTitle = intl.formatMessage(
    { id: "redblue.shell.loginPageTitle" },
    { centerTitle },
  );

  return {
    ...initialState?.settings,
    title: centerTitle,
    menu: {
      locale: true,
    },
    postMenuData: (menuData: LayoutMenuItem[] | undefined) => disableMenuItemTooltips(menuData) ?? [],
    pageTitleRender: (props: PageTitleRenderProps, defaultPageTitle?: string) => {
      const pathname = String(props?.location?.pathname || history.location.pathname || "");
      if (pathname === OIDC_CALLBACK_PATH || pathname === loginPath) {
        return buildDocumentTitle({
          kind: pathname === loginPath ? "login" : "callback",
          siteTitle,
          pageTitle: pathname === loginPath ? loginPageTitle : "",
        });
      }
      const pageTitleCandidates = [
        props?.pageName,
        defaultPageTitle,
        props?.title,
      ]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .filter((item) => item !== centerTitle && item !== siteTitle);
      return buildDocumentTitle({
        siteTitle,
        centerTitle,
        pageTitle: pageTitleCandidates[0] || centerTitle,
      });
    },
    onPageChange: () => {
      const pathname = readCurrentPathname();
      if (pathname === OIDC_CALLBACK_PATH) {
        return;
      }
      const redirectTarget = resolveUmiSsoGuardRedirect({
        initialStateReady: Boolean(initialState),
        pathname,
        search: readCurrentSearch(),
        hash: readCurrentHash(),
        loginPath,
        hasAccess,
        defaultAuthorizedPath: "/activities",
      });
      if (redirectTarget) {
        history.replace(redirectTarget);
        return;
      }
      if (pathname === "/") {
        history.replace("/activities");
      }
    },
    actionsRender: () => AdminShellActions({
      localeNode: <LocaleSwitch />,
      links: [
        {
          key: "portal",
          label: intl.formatMessage({ id: "redblue.shell.portalEntry" }),
          href: PORTAL_BASE_URL,
          icon: <AppstoreOutlined />,
        },
      ],
    }),
    avatarProps: buildAdminAvatarProps({
      currentUser: displayUser,
      onLogout: async () => {
        await logout();
          window.location.assign(toBrowserPath(`${loginPath}?logged_out=1`));
      },
    }) || {
      title: displayName,
    },
    childrenRender: (children: ReactNode) => (
      <ConfigProvider theme={{ cssVar: { key: "redblue-admin" } }}>
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    ),
  };
};

registerChunkLoadErrorReloadOnce("redblue-admin");
